import * as React from "react";
import { OAUTH_PROVIDERS, type OAuthProvider } from "@taskiq/shared";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/lib/auth-context";
import { describeAuthError } from "@/lib/auth-errors";
import { FormMessage } from "@/components/ui/Feedback";

/**
 * Google / Microsoft / Apple sign-in.
 *
 * Rendered on both the sign-in and sign-up screens, because from the provider's
 * side they are the same operation: a new identity is created if there is not
 * one already, and the database trigger then either finds an invitation or
 * leaves the user on the onboarding screen. Offering "sign up with Google" and
 * "sign in with Google" as separate flows would be two buttons that call the
 * same function.
 */

export function OAuthButtons({ label = "Or continue with" }: { label?: string }) {
  const { signInWithProvider } = useAuth();
  const [pending, setPending] = React.useState<OAuthProvider | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  async function start(provider: OAuthProvider) {
    setError(null);
    setPending(provider);
    try {
      // Redirects away and does not come back. The spinner stays up until the
      // browser navigates, which is the honest thing to show - clearing it would
      // make a slow redirect look like a click that did nothing.
      await signInWithProvider(provider);
    } catch (err) {
      setError(describeAuthError(err));
      setPending(null);
    }
  }

  return (
    <div className="oauth">
      <div className="oauth__divider">
        <span className="oauth__rule" />
        <span className="oauth__label">{label}</span>
        <span className="oauth__rule" />
      </div>

      {/* Three across rather than three stacked. At 44px each, stacked is 150px
          of a viewport this layout is trying not to overflow, and the provider
          mark alone identifies the button - the word "Continue" on all three
          says nothing that the row above has not already said. */}
      <div className="oauth__grid">
        {OAUTH_PROVIDERS.map((provider) => (
          <Button
            key={provider.id}
            variant="secondary"
            block
            loading={pending === provider.id}
            disabled={pending !== null && pending !== provider.id}
            onClick={() => void start(provider.id)}
            icon={<ProviderMark provider={provider.id} />}
            aria-label={`Continue with ${provider.label}`}
            title={`Continue with ${provider.label}`}
          >
            <span className="oauth__provider-label">{provider.label}</span>
          </Button>
        ))}
      </div>

      <FormMessage>{error}</FormMessage>
    </div>
  );
}

/**
 * Brand marks, inline.
 *
 * Drawn rather than fetched because the API's CSP forbids external hosts and,
 * more practically, a sign-in button whose logo is still loading is a sign-in
 * button people hesitate over. Each is the provider's own glyph in its own
 * colours - these are the one place in the console where a raw colour is
 * correct, because they are somebody else's brand and are not ours to
 * re-tokenise.
 */
function ProviderMark({ provider }: { provider: OAuthProvider }) {
  const className = "size-5 shrink-0";

  if (provider === "google") {
    return (
      <svg className={className} viewBox="0 0 24 24" aria-hidden focusable="false">
        <path
          fill="#4285F4"
          d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.46a5.52 5.52 0 0 1-2.4 3.62v3.01h3.88c2.27-2.09 3.58-5.17 3.58-8.82Z"
        />
        <path
          fill="#34A853"
          d="M12 24c3.24 0 5.96-1.08 7.94-2.91l-3.88-3.01c-1.08.72-2.45 1.15-4.06 1.15-3.13 0-5.78-2.11-6.73-4.95H1.26v3.11A12 12 0 0 0 12 24Z"
        />
        <path
          fill="#FBBC05"
          d="M5.27 14.28a7.2 7.2 0 0 1 0-4.56V6.61H1.26a12 12 0 0 0 0 10.78l4.01-3.11Z"
        />
        <path
          fill="#EA4335"
          d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.26 6.61l4.01 3.11C6.22 6.88 8.87 4.75 12 4.75Z"
        />
      </svg>
    );
  }

  if (provider === "azure") {
    return (
      <svg className={className} viewBox="0 0 24 24" aria-hidden focusable="false">
        <path fill="#F25022" d="M1 1h10.2v10.2H1z" />
        <path fill="#7FBA00" d="M12.8 1H23v10.2H12.8z" />
        <path fill="#00A4EF" d="M1 12.8h10.2V23H1z" />
        <path fill="#FFB900" d="M12.8 12.8H23V23H12.8z" />
      </svg>
    );
  }

  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden focusable="false">
      {/* Apple's mark is monochrome by specification, so it takes the button's
          own ink colour and stays legible in both themes. */}
      <path
        fill="currentColor"
        d="M17.05 12.54c-.03-2.6 2.12-3.85 2.22-3.91-1.21-1.77-3.09-2.01-3.76-2.04-1.6-.16-3.12.94-3.93.94-.81 0-2.06-.92-3.39-.9-1.74.03-3.35 1.01-4.24 2.57-1.81 3.14-.46 7.79 1.3 10.34.86 1.25 1.88 2.65 3.22 2.6 1.29-.05 1.78-.83 3.34-.83s2 .83 3.37.81c1.39-.03 2.27-1.27 3.12-2.53.98-1.45 1.39-2.86 1.41-2.93-.03-.01-2.7-1.04-2.73-4.12M14.5 4.71c.71-.86 1.19-2.06 1.06-3.25-1.02.04-2.26.68-3 1.54-.66.76-1.24 1.98-1.08 3.14 1.14.09 2.3-.58 3.02-1.43"
      />
    </svg>
  );
}
